import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { LivePill } from '@/components/ui';
import LoginPage from '@/pages/login';
import DashboardPage from '@/pages/dashboard';
import PerformancePage from '@/pages/performance';
import CredentialsPage from '@/pages/credentials';
import AccountDetailPage from '@/pages/account-detail';

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  if (loading) return <div className="grid h-full place-items-center text-text-dim">…</div>;

  if (!session) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Admin = JWT app_metadata.role === 'admin' (mirrors veritas.is_admin() and
  // useCurrentUser). The Credentials page is an admin tool (issue/assign EA
  // credentials), so it's admin-only — gated at both the nav and the route.
  const isAdmin =
    (session.user.app_metadata as { role?: string } | null)?.role === 'admin';

  return (
    <div className="flex h-full flex-col">
      <Header email={session.user.email ?? ''} isAdmin={isAdmin} />
      <main className="mx-auto w-full max-w-[1280px] flex-1 px-4 py-5 sm:px-6 sm:py-7 md:px-8 md:py-8">
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/performance" element={<PerformancePage />} />
          <Route path="/accounts/:login" element={<AccountDetailPage />} />
          <Route
            path="/credentials"
            element={isAdmin ? <CredentialsPage /> : <Navigate to="/dashboard" replace />}
          />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function Header({ email, isAdmin }: { email: string; isAdmin: boolean }) {
  const loc = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Close the slide-down menu when the route changes (so tapping a tab
  // collapses the nav back).
  useEffect(() => { setMenuOpen(false); }, [loc.pathname]);

  const tab = (to: string, label: string) => {
    const active = loc.pathname.startsWith(to);
    return (
      <Link
        to={to}
        className={cn(
          'rounded px-3 py-2 text-[11px] font-semibold uppercase tracking-eyebrow transition-colors',
          'min-h-[40px] inline-flex items-center',
          active ? 'bg-bg-2 text-text' : 'text-text-dim hover:bg-bg-1 hover:text-text',
        )}
      >
        {label}
      </Link>
    );
  };

  return (
    <header className="sticky top-0 z-30 border-b border-line bg-bg/80 backdrop-blur-md">
      <div className="mx-auto flex w-full max-w-[1280px] items-center gap-3 px-4 py-3 sm:gap-4 sm:px-6 md:gap-6 md:px-8">
        {/* Brand — compact on mobile, full on tablet+ */}
        <Link to="/dashboard" className="flex items-center gap-2 sm:gap-3">
          <span className="text-[20px] font-bold leading-none text-green">▽</span>
          <span className="hidden text-[14px] font-extrabold tracking-wordmark sm:inline">
            VALEXOS
          </span>
          <span className="hidden text-text-dim sm:inline">/</span>
          <span className="text-[13px] font-semibold tracking-wider text-text sm:text-[12px]">
            VERITAS
          </span>
        </Link>

        {/* LIVE pill — hidden on phones, the dot in the page itself does the same job */}
        <span className="hidden sm:inline-flex">
          <LivePill>LIVE</LivePill>
        </span>

        {/* Desktop nav */}
        <nav className="ml-2 hidden items-center gap-1 sm:flex">
          {tab('/dashboard', 'Accounts')}
          {tab('/performance', 'Performance')}
          {isAdmin && tab('/credentials', 'Credentials')}
        </nav>

        {/* Right side: email (tablet+) + sign-out (tablet+) OR hamburger (mobile) */}
        <div className="ml-auto flex items-center gap-2 text-[11px] text-text-mid sm:gap-3">
          <span className="hidden md:inline">{email}</span>
          <button
            onClick={() => supabase.auth.signOut()}
            className="hidden rounded-md border border-line px-3 py-2 text-text hover:bg-bg-1 sm:inline-flex sm:min-h-[40px] sm:items-center"
          >
            Sign out
          </button>

          {/* Mobile-only hamburger */}
          <button
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-line text-text hover:bg-bg-1 sm:hidden"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile slide-down nav. Stays inside the header so taps outside
          dismiss it via the route-change effect (and any nav click). */}
      {menuOpen && (
        <div className="border-t border-line bg-bg-1 sm:hidden">
          <nav className="mx-auto flex max-w-[1280px] flex-col gap-1 px-4 py-3">
            <Link
              to="/dashboard"
              className={cn(
                'rounded-md px-3 py-3 text-[12px] font-semibold uppercase tracking-eyebrow',
                loc.pathname.startsWith('/dashboard')
                  ? 'bg-bg-2 text-text'
                  : 'text-text-dim hover:bg-bg-2 hover:text-text',
              )}
            >
              Accounts
            </Link>
            <Link
              to="/performance"
              className={cn(
                'rounded-md px-3 py-3 text-[12px] font-semibold uppercase tracking-eyebrow',
                loc.pathname.startsWith('/performance')
                  ? 'bg-bg-2 text-text'
                  : 'text-text-dim hover:bg-bg-2 hover:text-text',
              )}
            >
              Performance
            </Link>
            {isAdmin && (
              <Link
                to="/credentials"
                className={cn(
                  'rounded-md px-3 py-3 text-[12px] font-semibold uppercase tracking-eyebrow',
                  loc.pathname.startsWith('/credentials')
                    ? 'bg-bg-2 text-text'
                    : 'text-text-dim hover:bg-bg-2 hover:text-text',
                )}
              >
                Credentials
              </Link>
            )}
            <div className="mt-1 border-t border-line pt-2">
              <div className="px-3 py-1 text-[10px] uppercase tracking-eyebrow text-text-dim">
                {email}
              </div>
              <button
                onClick={() => supabase.auth.signOut()}
                className="w-full rounded-md px-3 py-3 text-left text-[12px] font-semibold uppercase tracking-eyebrow text-red hover:bg-bg-2"
              >
                Sign out
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
